import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { EntryPass } from '../../../../../../../models/entry-pass';
import {
  StaffSiteSale,
  StaffSiteSaleLineItem,
} from '../../../../../../../models/staff-site-sale';
import { StaffSiteSaleService } from '../../../../../../../services/staff-site-sale/staff-site-sale.service';
import { LineItemPurchaseType } from '../../../../../../../models/payment-enums';
import { dateStringFromISO8601, propExists } from '../../../../../../../utils';
import { SelectCustomerModalComponent } from '../../../../modals/select-customer-modal/select-customer-modal.component';
import { Customer, CustomerSearchResult } from '../../../../../../../models/customer';
import {
  CreateNewCustomerModalComponent
} from '../../../../customers/modals/create-new-customer-modal/create-new-customer-modal.component';


@Component({
  selector: 'app-add-entry-pass-line-item-to-sale-modal',
  templateUrl: './add-entry-pass-line-item-to-sale-modal.component.html',
  styleUrls: ['./add-entry-pass-line-item-to-sale-modal.component.css']
})
export class AddEntryPassLineItemToSaleModalComponent implements OnInit {
  @Input() sale: StaffSiteSale;
  @Input() entryPasses: EntryPass[];
  @Input() lineItemToCopy: StaffSiteSaleLineItem | null = null;
  @Input() replaceExistingLineItem = false;
  @Input() savedCallback: (updatedSale: StaffSiteSale) => undefined;

  public lineItem: StaffSiteSaleLineItem = {
    id: null,
    purchase_type: LineItemPurchaseType.entryPass,
    description: '',
    product_type_id: null,
    product_id: null,
    product_location_id: null,
    product_location_name: null,
    product_start_date: null,
    product_end_date: null,
    customer_id: null,
    customer_first_name: null,
    customer_last_name: null,
    customer_birthday: null,
    customer_email: null,
    customer_profile_image_url: null,
    customer_is_checked_in: false,
    amount_pre_tax: null,
    tax_amount: null,
    discount_amount: null,
    amount: null,
    deposit_amount: null,
    terms_and_conditions_checkbox_text: '',
    add_on_ids: [],
    add_on_names: [],
    number_of_uses_remaining: null,
    customers_checked_in_using_product: [],
    lightspeed_product_id: '',
    discounts: [],
    order_number: null,
  };

  public loadingSubmit = false;
  public errorMessage = '';
  public serverError: number = null;

  public readonly dateStringFromISO8601 = dateStringFromISO8601;

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private staffSiteSaleService: StaffSiteSaleService,
  ) {}

  ngOnInit() {
    if (propExists(this.lineItemToCopy)) {
      this.lineItem.product_type_id = this.lineItemToCopy.product_type_id;
      this.lineItem.customer_id = this.lineItemToCopy.customer_id;
      this.lineItem.customer_first_name = this.lineItemToCopy.customer_first_name;
      this.lineItem.customer_last_name = this.lineItemToCopy.customer_last_name;
      this.lineItem.customer_birthday = this.lineItemToCopy.customer_birthday;
      this.lineItem.customer_email = this.lineItemToCopy.customer_email;
      if (this.replaceExistingLineItem) {
        this.lineItem.order_number = this.lineItemToCopy.order_number;
      }
    } else {
      this.lineItem.customer_id = this.sale.customer_id;
      this.lineItem.customer_first_name = this.sale.customer_first_name;
      this.lineItem.customer_last_name = this.sale.customer_last_name;
      this.lineItem.customer_birthday = this.sale.customer_birthday;
      this.lineItem.customer_email = this.sale.customer_email;
    }
  }

  public purchasingCustomerIsAssigned() {
    return (this.lineItem.customer_id === this.sale.customer_id);
  }

  public assignPurchasingCustomer() {
    this.lineItem.customer_id = this.sale.customer_id;
    this.lineItem.customer_first_name = this.sale.customer_first_name;
    this.lineItem.customer_last_name = this.sale.customer_last_name;
    this.lineItem.customer_birthday = this.sale.customer_birthday;
    this.lineItem.customer_email = this.sale.customer_email;
  }

  public openSelectCustomerModal() {
    const modalRef = this.modalService.open(SelectCustomerModalComponent);
    modalRef.componentInstance.submitCallback = (result: CustomerSearchResult) => {
      this.lineItem.customer_id = result.id;
      this.lineItem.customer_first_name = result.first_name;
      this.lineItem.customer_last_name = result.last_name;
      this.lineItem.customer_birthday = result.birthday;
      this.lineItem.customer_email = result.email;
    };
  }

  public openCreateCustomerModal() {
    const modalRef = this.modalService.open(CreateNewCustomerModalComponent);
    modalRef.componentInstance.savedCallback = (result: Customer) => {
      this.lineItem.customer_id = result.id;
      this.lineItem.customer_first_name = result.first_name;
      this.lineItem.customer_last_name = result.last_name;
      this.lineItem.customer_birthday = result.birthday;
      this.lineItem.customer_email = result.email;
    };
  }

  public allFieldsEntered(): boolean {
    return (
      propExists(this.lineItem.product_type_id) &&
      propExists(this.lineItem.customer_id)
    );
  }

  public handleSubmit() {
    if (!this.allFieldsEntered()) {
      return;
    }

    this.loadingSubmit = true;

    this.staffSiteSaleService
      .createLineItem(this.sale.id, this.lineItem)
      .then(saleWithCreated => {
        if (this.replaceExistingLineItem) {
          return this.staffSiteSaleService
            .removeLineItem(
              this.sale.id,
              this.lineItemToCopy.id,
            )
            .then(saleWithRemoval => {
              this.savedCallback(saleWithRemoval);
              this.activeModal.close();
            });
        } else {
          this.savedCallback(saleWithCreated);
          this.activeModal.close();
        }
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errorMessage = reason.error.useful_error_message;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }
}
